import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useDialog } from "../../../contexts/DialogContext";
import { getTimeStamp } from "../../../utils/common";
import { useTranslation } from "react-i18next";
import useMainContentDimensions from "../../../utils/mainContentDimensions";

import {
  Card,
  CardHeader,
  Breadcrumbs as MuiBreadcrumbs,
  Paper as MuiPaper,
  Button,
  Typography,
  CardContent,
  Box,
  Grid,
  Divider,
} from "@mui/material";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import { DataGrid } from "@mui/x-data-grid";
import Toolbar from "../../../components/Toolbar";
import useWindowDimensions from "../../../utils/windowDimensions";
import useAuth from "../../../hooks/useAuth";
import useReport from "../../../hooks/useReports";
import AdvanceFilter from "./components/AdvanceFilter";
import Stats from "./../../../components/Stats";
import {
  searchPayload,
  convertedValue,
  formatDate,
  convertToDateString,
} from "../../../utils/common";
import moment from "moment";
import { Helmet } from "react-helmet-async";
import { roundOff } from "../../../utils/common";
import {
  CurrencyRupee as CurrencyRupeeIcon,
  AccountBalanceWallet as WalletIcon,
  ReceiptLong as ReceiptLongIcon,
  Payments as PaymentsIcon,
  PendingActions as PendingActionsIcon,
} from "@mui/icons-material";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);

function ComboReport(props) {
  const { user } = useAuth();

  const {
    getComboReport,
    comboReports,
    paging,
    downloadComboReport,
    downloadFileType,
    isLoading,
    isLoadingDownload,
  } = useReport();

  const { totalNumberOfRecords, hasNextPage, pageNumber, pageSize } = paging;

  const { width: mainContentWidth } = useMainContentDimensions();

  const { state } = useLocation();
  const { t } = useTranslation();

  const { radUserId } = props;

  const today = formatDate(new Date());

  const currentPayload = {
    filters: [
      {
        fieldName: "invoiceDate",
        fieldValue: `${today} 00:00:00,${today} 23:59:59`,
        op: "btn",
        dataType: "date",
        condition: "and",
        isJoin: false,
      },
    ],
    pagination: {
      pageNumber: 1,
      pageSize: 100,
    },
    sortFields: [
      {
        field: "createdAt",
        sort: "DESC",
      },
    ],
  };

  useEffect(() => {
    if (radUserId != undefined) {
      const payloadValues = {
        ...currentPayload,
        filters: [
          ...currentPayload.filters,
          {
            fieldName: "radUserId",
            fieldValue: radUserId,
            op: "eq",
            dataType: "int",
            condition: "and",
            isJoin: true,
            attributeName: "radUser",
            entityName: "RadUserEntity",
          },
        ],
      };
      setPayload(payloadValues);
    } else {
      setPayload(currentPayload);
    }
    setPaginationModel({
      page: 0,
      pageSize: 100,
    });
  }, [radUserId]);

  useEffect(() => {
    if (user != undefined && user.businessType == "USER") {
      const payloadValues = {
        ...currentPayload,
        filters: [
          ...currentPayload.filters,
          {
            fieldName: "radUserId",
            fieldValue: user.radUserId,
            op: "eq",
            dataType: "int",
            condition: "and",
            isJoin: true,
            attributeName: "radUser",
            entityName: "RadUserEntity",
          },
        ],
      };
      setPayload(payloadValues);
    } else {
      setPayload(currentPayload);
    }
  }, [user]);

  const [sortModel, setSortModel] = useState([
    {
      field: "updatedAt",
      sort: "desc",
    },
  ]);
  const [filterFormData, setFilterFormData] = useState({
    userName: "",
    tenantName: {},
    invoiceDate: [new Date(), new Date()],
    invoiceDateStr: `${today} 00:00:00,${today} 23:59:59`,
  });
  const { height } = useWindowDimensions();

  const { openDialog } = useDialog();

  const [payload, setPayload] = useState({});

  const [filterResetFunc, setFilterResetFunc] = useState(
    () => () => console.log("Refresh initialized"),
  );

  const fetchData = async () => {
    if (Object.keys(payload).length != 0) {
      await getComboReport(payload);
    }
  };

  const refreshPage = () => {
    if (radUserId != undefined) {
      const payloadValues = {
        ...currentPayload,
        filters: [
          ...currentPayload.filters,
          {
            fieldName: "radUserId",
            fieldValue: radUserId,
            op: "eq",
            dataType: "int",
            condition: "and",
            isJoin: true,
            attributeName: "radUser",
            entityName: "RadUserEntity",
          },
        ],
      };
      setPayload(payloadValues);
      setFilterFormData({
        userName: "",
        tenantName: {},
        invoiceDate: [new Date(), new Date()],
        invoiceDateStr: `${today} 00:00:00,${today} 23:59:59`,
      });
    } else {
      setPayload(currentPayload);
      setFilterFormData({
        userName: "",
        tenantName: {},
        invoiceDate: [new Date(), new Date()],
        invoiceDateStr: `${today} 00:00:00,${today} 23:59:59`,
      });
    }
    filterResetFunc();
    setPaginationModel({
      page: 0,
      pageSize: 100,
    });
  };

  const onPageChange = (newPaginationModel) => {
    console.log(newPaginationModel, "pagepage");

    const pagination = {
      pageNumber: newPaginationModel.page === 0 ? 1 : newPaginationModel.page + 1,
      pageSize: newPaginationModel.pageSize, // Update pageSize dynamically
    };

    setPayload((prev) => ({ ...prev, pagination }));
    setPaginationModel(newPaginationModel);
  };

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 100,
  });

  const paginationMetaRef = useRef();

  // Memoize to avoid flickering when the `hasNextPage` is `undefined` during refetch
  const paginationMeta = React.useMemo(() => {
    if (hasNextPage !== undefined && paginationMetaRef.current?.hasNextPage !== hasNextPage) {
      paginationMetaRef.current = { hasNextPage };
    }
    return paginationMetaRef.current;
  }, [hasNextPage]);

  const onPaginationModelChange = (newModel) => {
    setPaginationModel(newModel);

    const pagination = {
      pageNumber: newModel.page + 1, // API expects 1-based
      pageSize: newModel.pageSize,
    };

    setPayload((prev) => ({
      ...prev,
      pagination,
    }));
  };

  const breadCrump = () => {
    return (
      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Typography>Reports</Typography>
        <Typography>Combo Reports</Typography>
      </Breadcrumbs>
    );
  };

  useEffect(() => {
    fetchData();
  }, [payload]);

  const handleSortModelChange = (newModel) => {
    console.log(newModel, "newModel");
    if (newModel.length !== 0) {
      setSortModel(newModel);
      setPayload((prev) => ({ ...prev, sortFields: newModel }));
    }
  };

  const columns = [
    {
      field: "tenantName",
      headerName: "Franchise Name",
      width: 130,
    },
    {
      field: "userName",
      headerName: "UserName",
      width: 150,
    },
    {
      field: "fullName",
      headerName: "Full Name",
      width: 130,
    },
    {
      field: "invoiceNo",
      headerName: "Invoice No",
      width: 150,
    },
    {
      field: "gstNo",
      headerName: "Gst No",
      width: 150,
    },
    {
      field: "renewalType",
      headerName: "Renewal Type",
      width: 150,
    },
    {
      field: "userServiceType",
      headerName: "Service Type",
      width: 200,
    },
    {
      field: "planName",
      headerName: "Plan Name",
      width: 200,
    },
    {
      field: "rateName",
      headerName: "Rate",
      width: 150,
    },
    {
      field: "taxAmount",
      headerName: "Service Tax Amount",
      width: 150,
    },
    {
      field: "msoBalance",
      headerName: "MSO Balance",
      width: 150,
    },
    {
      field: "msoSharing",
      headerName: "MSO Share",
      width: 150,
    },
    {
      field: "msoSharingAmount",
      headerName: "MSO Share Amount",
      width: 150,
    },
    {
      field: "deductedLcoAmount",
      headerName: "Deducted LCO Amount",
      width: 150,
    },
    {
      field: "lcoBalance",
      headerName: "LCO Balance",
      width: 150,
    },
    {
      field: "lcoSharing",
      headerName: "LCO Share",
      width: 150,
    },
    {
      field: "lcoSharingAmount",
      headerName: "LCO Share Amount",
      width: 150,
    },
    {
      field: "expiration",
      headerName: "Expiration",
      valueGetter: (params) => (params ? moment(params).format("DD-MM-YYYY hh:mm a") : ""),
      width: 150,
    },
    {
      field: "invoiceStatus",
      headerName: "Invoice Status",
      width: 150,
    },
    {
      field: "invoiceDate",
      headerName: "Invoice Date",
      width: 150,
    },
    {
      field: "collectionDate",
      headerName: "Collection Date",
      valueGetter: (params) => (params ? moment(params).format("DD-MM-YYYY hh:mm a") : ""),
      width: 180,
    },
    {
      field: "createdBy",
      headerName: "Created By",
      width: 150,
    },
  ];

  const openFilterDialog = () => {
    return openDialog(
      "advance_filter",
      "Advance Filter",
      <AdvanceFilter
        payload={payload}
        setPayload={setPayload}
        setFilterResetFunc={setFilterResetFunc}
        filterFormData={filterFormData}
        setFilterFormData={setFilterFormData}
      />,
    );
  };
  const exportFile = async (fileType) => {
    await downloadComboReport(fileType, payload);
  };

  return (
    <React.Fragment>
      <Helmet title="SaaS Dashboard" />
      <Grid justifyContent="space-between" container spacing={6}>
        <Grid item>
          <Typography variant="h4" gutterBottom>
            Combo Report Statistics
          </Typography>
        </Grid>
      </Grid>
      <Divider my={4} />
      <Grid container spacing={6}>
        <Grid item xs={12} sm={12} md={6} lg={3} xl>
          <Stats
            title="Plan Amount"
            amount={"₹" + roundOff(comboReports?.comboReportSum?.planAmount)}
            chip="Monthly"
            bgcolor="#3b82f6"
            icon={CurrencyRupeeIcon}
          />
        </Grid>

        <Grid item xs={12} sm={12} md={6} lg={3} xl>
          <Stats
            title="Total Amount"
            amount={"₹" + roundOff(comboReports?.comboReportSum?.totalAmount)}
            chip="Weekly"
            bgcolor="#10b981"
            icon={WalletIcon}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={3} xl>
          <Stats
            title="Tax Amt"
            amount={"₹" + roundOff(comboReports?.comboReportSum?.taxAmount)}
            chip="Weekly"
            bgcolor="#8b5cf6"
            icon={ReceiptLongIcon}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={3} xl>
          <Stats
            title="Paid Amount"
            amount={"₹" + roundOff(comboReports?.comboReportSum?.paidAmount)}
            chip="Weekly"
            bgcolor="#0ea5e9"
            icon={PaymentsIcon}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={3} xl>
          <Stats
            title="Unpaid Amount"
            amount={"₹" + roundOff(comboReports?.comboReportSum?.unpaidAmount)}
            chip="Weekly"
            bgcolor="#f43f5e"
            icon={PendingActionsIcon}
          />
        </Grid>
      </Grid>
      <Card sx={{ width: "100%", maxWidth: mainContentWidth - 64 }}>
        <CardHeader
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" }, // Stack on small screens
            justifyContent: "space-between",
            alignItems: "flex-start", // Adjust alignment as needed
          }}
          action={
            <Toolbar
              downloadFile={exportFile}
              refreshPage={refreshPage}
              openFilterDialog={openFilterDialog}
              isLoadingDownload={isLoadingDownload}
              downloadFileType={downloadFileType}
            />
          }
          title={
            <Typography variant="h6" sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
              {t("combo_reports")}
            </Typography>
          }
          subheader={<Box sx={{ display: "flex", flexWrap: "wrap" }}>{breadCrump()}</Box>}
        />
        <CardContent sx={{ height: height - 180 }}>
          <DataGrid
            columns={columns}
            rows={comboReports.data}
            getRowId={(row) => row["renewId"]}
            rowCount={comboReports.paging.totalNumberOfRecords}
            loading={isLoading}
            rowsPerPageOptions={[5]}
            pagination
            page={comboReports.paging.pageNumber}
            pageSize={comboReports.paging.pageSize}
            paginationMode="server"
            onPaginationModelChange={onPageChange}
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            paginationMeta={paginationMeta}
            paging={{
              pageNumber: comboReports.paging.pageNumber,
              pageSize: comboReports.paging.pageSize,
            }}
            paginationModel={paginationModel}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row"
            }
            sx={{
              "& .MuiDataGrid-row.even-row": {
                backgroundColor: "#f9f9f9", // Light gray for even rows
              },
              "& .MuiDataGrid-row.odd-row": {
                backgroundColor: "#ffffff", // White for odd rows
              },
            }}
          />
        </CardContent>
      </Card>
    </React.Fragment>
  );
}

export default ComboReport;
